/**
 * Chatbot conversation flow — pure data config.
 *
 * Step shape:
 * {
 *   id         : string       – unique step identifier
 *   type       : 'message'    – bot message, advances automatically after `delay` ms
 *             | 'options'     – presents clickable chips
 *             | 'text-input'  – free-text field + Skip
 *             | 'rating'      – 1-5 star picker + Skip
 *   message    : string       – what the bot says
 *   options    : array        – [{ label, value, icon?, action? }]  (type='options' only)
 *   next       : string | (answer) => string   – next step id, or fn for branching
 *   delay      : number       – ms before auto-advancing (type='message' only, default 1200)
 *   skippable  : boolean      – show Skip chip / link (auto-set for text-input & rating)
 * }
 *
 * Special option actions (handled in Chatbot.jsx):
 *   'view-resume'     – open resume URL in new tab
 *   'download-resume' – trigger resume download
 *
 * Full flow paths:
 *   Recruiter  → resume → ask-name-rec → ask-company → rating → [feedback?] → farewell
 *   Developer  → dev-message → ask-name-dev → ask-github → rating → [feedback?] → farewell
 *   Exploring  → ask-name-gen → rating → [feedback?] → farewell
 *   Skip       → rating → [feedback?] → farewell
 *
 * Returning user (chatbot_sent already true):
 *   welcome-back → rating-back → [feedback-back?] → farewell-back
 */

const STEPS = [
  // ── Common entry ──────────────────────────────────────────────────────────
  {
    id: "welcome",
    type: "message",
    message: "Hi! Welcome to my portfolio 😉",
    delay: 1200,
    next: "who-are-you",
  },
  {
    id: "who-are-you",
    type: "options",
    message: "May I know who you are? 🤔",
    options: [
      { label: "Recruiter",      value: "Recruiter",  icon: "bx bx-briefcase" },
      { label: "Developer",      value: "Developer",  icon: "bx bx-code-alt" },
      { label: "Just exploring", value: "Exploring",  icon: "uil uil-compass" },
      { label: "Skip",           value: "__skip__" },
    ],
    next: (answer) => {
      if (answer === "Recruiter") return "resume";
      if (answer === "Developer") return "dev-message";
      if (answer === "Just exploring") return "ask-name-gen";
      return "rating"; // skip
    },
  },

  // ── Recruiter path ────────────────────────────────────────────────────────
  {
    id: "resume",
    type: "options",
    message: "Want quick access to my resume? 📄",
    options: [
      { label: "View",     value: "Viewed resume",     action: "view-resume",     icon: "uil uil-eye" },
      { label: "Download", value: "Downloaded resume", action: "download-resume", icon: "uil uil-download-alt" },
      { label: "Skip",     value: "__skip__" },
    ],
    next: "ask-name-rec",
  },
  {
    id: "ask-name-rec",
    type: "text-input",
    message: "May I know your name? 😊",
    placeholder: "Your name…",
    skippable: true,
    next: "ask-company",
  },
  {
    id: "ask-company",
    type: "text-input",
    message: "Mind sharing which company or organization you're from? 🏢",
    placeholder: "Company name…",
    skippable: true,
    next: "rating",
  },

  // ── Developer path ────────────────────────────────────────────────────────
  {
    id: "dev-message",
    type: "message",
    message: "Awesome! Feel free to explore my projects and skills 🚀",
    delay: 1400,
    next: "ask-name-dev",
  },
  {
    id: "ask-name-dev",
    type: "text-input",
    message: "May I know your name — or drop your LinkedIn if you'd like to connect? 🤝",
    placeholder: "Name or linkedin.com/in/…",
    skippable: true,
    next: "ask-github",
  },
  {
    id: "ask-github",
    type: "text-input",
    message: "Got a GitHub or portfolio to share? 🐙",
    placeholder: "github.com/… or URL",
    skippable: true,
    next: "rating",
  },

  // ── Exploring path ────────────────────────────────────────────────────────
  {
    id: "ask-name-gen",
    type: "text-input",
    message: "May I know your name? 😊",
    placeholder: "Your name…",
    skippable: true,
    next: "rating",
  },

  // ── Common tail ───────────────────────────────────────────────────────────
  {
    id: "rating",
    type: "rating",
    message: "How would you rate this portfolio? 🌟",
    skippable: true,
    // Route to feedback if 1–2 stars, otherwise farewell
    next: (answer) => {
      if (!answer) return "farewell";
      const stars = (answer.match(/★/g) || []).length;
      return stars <= 2 ? "feedback" : "farewell";
    },
  },
  {
    id: "feedback",
    type: "text-input",
    message: "Sorry to hear that! I'd love to improve — any suggestions? 🙏",
    placeholder: "What could be better…",
    skippable: true,
    next: "farewell",
  },
  {
    id: "farewell",
    type: "message",
    message: "Thanks for visiting! 🎉",
    delay: 1400,
    next: null, // terminal — triggers auto-close
  },

  // ── Returning user (chatbot_sent already true) ────────────────────────────
  {
    id: "welcome-back",
    type: "message",
    message: "Welcome back! Great to see you again 👋",
    delay: 1400,
    next: "rating-back",
  },
  {
    id: "rating-back",
    type: "rating",
    message: "Would you like to update your rating? ⭐",
    skippable: true,
    next: (answer) => {
      if (!answer) return "farewell-back";
      const stars = (answer.match(/★/g) || []).length;
      return stars <= 2 ? "feedback-back" : "farewell-back";
    },
  },
  {
    id: "feedback-back",
    type: "text-input",
    message: "Sorry to hear that! Any suggestions on what I can improve?",
    placeholder: "Your feedback…",
    skippable: true,
    next: "farewell-back",
  },
  {
    id: "farewell-back",
    type: "message",
    message: "Thanks again for stopping by! 🎉",
    delay: 2200,
    next: null,
  },
];

export default STEPS;
